import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import type { Settings } from '../api/types';
import { t } from '../i18n';
import { describeError } from '../lib/errors';
import { formatDateTime } from '../lib/format';
import { useLang } from '../lib/hooks';
import { ErrorBox, Field, Notice, Section, Spinner } from '../components/ui';

const PROVIDERS = ['none', 'ollama', 'deepl', 'libretranslate'] as const;

const LANGUAGES: ReadonlyArray<readonly [string, string]> = [
  ['auto', 'Auto'],
  ['en', 'English'],
  ['ru', 'Русский'],
  ['ja', '日本語'],
  ['ko', '한국어'],
  ['zh', '中文'],
  ['es', 'Español'],
  ['de', 'Deutsch'],
  ['fr', 'Français'],
  ['it', 'Italiano'],
  ['pt', 'Português'],
  ['pl', 'Polski'],
  ['uk', 'Українська'],
  ['tr', 'Türkçe'],
];

export function SettingsPage() {
  useLang();

  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [health, setHealth] = useState<string>('—');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getSettings()
      .then((value) => {
        if (!cancelled) setSettings(value);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(describeError(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    api
      .health()
      .then((value) => {
        if (cancelled) return;
        const status = typeof value.status === 'string' ? value.status : 'ok';
        const version = typeof value.version === 'string' ? ` · ${value.version}` : '';
        setHealth(`${status}${version}`);
      })
      .catch(() => {
        if (!cancelled) setHealth(t('health.fail'));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (notice === null) return;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const patch = (changes: Partial<Settings>) => {
    if (settings === null) return;
    setSettings({ ...settings, ...changes });
  };

  const save = async (event: Event) => {
    event.preventDefault();
    if (settings === null || saving) return;

    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.updateSettings({
        source_language: settings.source_language,
        target_language: settings.target_language,
        translation_provider: settings.translation_provider,
        interface_language: settings.interface_language,
        show_native_transcription: settings.show_native_transcription,
        auto_save_words: settings.auto_save_words,
        subtitle_font_scale: settings.subtitle_font_scale,
        subtitle_opacity: settings.subtitle_opacity,
        auto_pause: settings.auto_pause,
        anki_enabled: settings.anki_enabled,
        anki_deck: settings.anki_deck,
        anki_model: settings.anki_model,
        anki_tags: settings.anki_tags,
      });
      setSettings(updated);
      setNotice(t('common.saved'));
    } catch (caught) {
      setSaveError(describeError(caught));
    } finally {
      setSaving(false);
    }
  };

  if (loading && settings === null) return <Spinner />;
  if (error !== null) return <ErrorBox message={error} onRetry={() => window.location.reload()} />;
  if (settings === null) return null;

  return (
    <div class="page">
      <header class="page__header">
        <div>
          <h1>{t('settings.title')}</h1>
          <p class="muted">{t('settings.subtitle')}</p>
        </div>
      </header>

      {notice ? <Notice tone="ok">{notice}</Notice> : null}
      {saveError ? <ErrorBox message={saveError} /> : null}

      <form class="page__form" onSubmit={save}>
        <Section title={t('health.base')}>
          <dl class="kv">
            <dt>{t('health.base')}</dt>
            <dd>
              <code>{window.location.origin}</code>
            </dd>
            <dt>{t('anki.status')}</dt>
            <dd>{health}</dd>
            <dt>{t('settings.updatedAt', { date: '' })}</dt>
            <dd>{formatDateTime(settings.updated_at)}</dd>
          </dl>
        </Section>

        <Section title={t('settings.languages')}>
          <div class="grid grid--3">
            <Field label={t('settings.source')} htmlFor="set-source">
              <select
                id="set-source"
                value={settings.source_language}
                onChange={(event) => patch({ source_language: event.currentTarget.value })}
              >
                {LANGUAGES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('settings.target')} htmlFor="set-target">
              <select
                id="set-target"
                value={settings.target_language}
                onChange={(event) => patch({ target_language: event.currentTarget.value })}
              >
                {LANGUAGES.filter(([code]) => code !== 'auto').map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('settings.provider')} htmlFor="set-provider">
              <select
                id="set-provider"
                value={settings.translation_provider ?? 'none'}
                onChange={(event) => patch({ translation_provider: event.currentTarget.value })}
              >
                {PROVIDERS.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('settings.interface')} htmlFor="set-interface-lang">
              <select
                id="set-interface-lang"
                value={settings.interface_language}
                onChange={(event) => patch({ interface_language: event.currentTarget.value })}
              >
                <option value="ru">Русский</option>
                <option value="en">English</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title={t('settings.playback')}>
          <div class="grid grid--2">
            <Field label={`${t('settings.fontScale')} — ${settings.subtitle_font_scale.toFixed(1)}×`} htmlFor="set-scale">
              <input
                id="set-scale"
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={settings.subtitle_font_scale}
                onInput={(event) => patch({ subtitle_font_scale: Number(event.currentTarget.value) })}
              />
            </Field>
            <Field label={`${t('settings.opacity')} — ${Math.round(settings.subtitle_opacity * 100)}%`} htmlFor="set-opacity">
              <input
                id="set-opacity"
                type="range"
                min="0.1"
                max="1"
                step="0.05"
                value={settings.subtitle_opacity}
                onInput={(event) => patch({ subtitle_opacity: Number(event.currentTarget.value) })}
              />
            </Field>
          </div>
          <div class="checks">
            <label class="check">
              <input
                type="checkbox"
                checked={settings.auto_pause}
                onChange={(event) => patch({ auto_pause: event.currentTarget.checked })}
              />
              <span>{t('settings.autoPause')}</span>
            </label>
            <label class="check">
              <input
                type="checkbox"
                checked={settings.show_native_transcription}
                onChange={(event) => patch({ show_native_transcription: event.currentTarget.checked })}
              />
              <span>{t('settings.showNative')}</span>
            </label>
            <label class="check">
              <input
                type="checkbox"
                checked={settings.auto_save_words}
                onChange={(event) => patch({ auto_save_words: event.currentTarget.checked })}
              />
              <span>{t('settings.autoSave')}</span>
            </label>
          </div>
        </Section>

        <Section title={t('settings.anki')}>
          <label class="check">
            <input
              type="checkbox"
              checked={settings.anki_enabled}
              onChange={(event) => patch({ anki_enabled: event.currentTarget.checked })}
            />
            <span>{t('settings.ankiEnabled')}</span>
          </label>
          <div class="grid grid--3">
            <Field label={t('settings.deck')} htmlFor="set-deck">
              <input
                id="set-deck"
                type="text"
                maxLength={128}
                value={settings.anki_deck}
                onInput={(event) => patch({ anki_deck: event.currentTarget.value })}
              />
            </Field>
            <Field label={t('settings.model')} htmlFor="set-model">
              <input
                id="set-model"
                type="text"
                maxLength={128}
                value={settings.anki_model}
                onInput={(event) => patch({ anki_model: event.currentTarget.value })}
              />
            </Field>
            <Field label={t('settings.tags')} htmlFor="set-tags">
              <input
                id="set-tags"
                type="text"
                maxLength={255}
                value={settings.anki_tags}
                onInput={(event) => patch({ anki_tags: event.currentTarget.value })}
              />
            </Field>
          </div>
        </Section>

        <div class="page__footer-actions">
          <button type="submit" class="btn btn--primary" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
